import type { ReactNode } from 'react';

export interface FieldControlProps {
  id: string;
  value: unknown;
  disabled: boolean;
  'aria-invalid': boolean;
  'aria-describedby': string | undefined;
  onValueChange(value: unknown): void;
  onBlur(): void;
}

export function FormField({
  id,
  label,
  optional,
  hint,
  error,
  children,
}: {
  id: string;
  label: string;
  optional?: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div className="pc-form-field" data-invalid={error ? '' : undefined}>
      <label className="pc-form-label" htmlFor={id}>
        {label}
        {optional && <span className="pc-form-optional">{optional}</span>}
      </label>
      {hint && (
        <p className="pc-form-hint" id={`${id}-hint`}>
          {hint}
        </p>
      )}
      {children}
      {error && (
        <p className="pc-form-error" id={`${id}-error`}>
          {error}
        </p>
      )}
    </div>
  );
}

// Precise decimals/integers remain the person's original string. The server
// validates the C1 domain; the browser never converts them to binary numbers.
export function TextField({
  inputMode,
  readOnly,
  ...props
}: FieldControlProps & {
  inputMode?: 'text' | 'decimal' | 'numeric' | 'email';
  readOnly?: boolean;
}) {
  const { value, onValueChange, ...inputProps } = props;
  return (
    <span
      className="pc-field pc-field-md"
      data-state={props.disabled ? 'disabled' : readOnly ? 'readonly' : undefined}
    >
      <input
        {...inputProps}
        className="pc-field-input"
        type="text"
        inputMode={inputMode}
        readOnly={readOnly}
        value={typeof value === 'string' ? value : ''}
        onChange={(event) => onValueChange(event.currentTarget.value)}
      />
    </span>
  );
}
